import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MiscModel } from './../../../../_models/misc-model';

import { CommonModule } from '@angular/common';
import { DataTranslationModel } from '@models/data-translation-model';
import { VariantColorFromApiModel } from '@models/variant-color-from-api';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTranslatePipe } from '@pipes/data-translate.pipe';
import { ComponentVariantFromApiModel } from '@models/api-models/component-variant-fromapi';
import { ColorModel } from '@models/color';
import { ColorGroupModel } from '@models/color-group';
import { VariantColorModel } from '@models/variant-color';
import { ImageService } from '../../../../_services/image.service';
import { SorterService } from '../../../../_services/sorter.service';
import { CustomizerColorItemComponent } from './customizer-color-item/customizer-color-item';

@Component({
	selector: 'customizer-colors-auto-update',
	templateUrl: 'customizer-colors.html',
	styleUrls: ['./_customizer-colors.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, DataTranslatePipe, TranslatePipe, CustomizerColorItemComponent]
})
export class CustomizerColorsAutoUpdateComponent implements OnInit, AfterViewInit {

	@Input() specialColorText: string;
	@Input() editMode: boolean;
	@Input() extendedInformation: boolean;
	@Input() extendedInformationTable?: boolean = true;
	@Input() translateName: string = "COLOR.NOCOLOR";
	@Input() imageClass: string;
	@Input() matchableColors: Array<ColorModel | null>;
	@Input() matchableMisc: Array<MiscModel>;
	@Input() useColorGroup: boolean = true;
	@Input() variants: Array<ComponentVariantFromApiModel>;
	@Input() selected?: VariantColorModel | null;
	@Input() selectedColorGroup?: ColorGroupModel | null = null;
	@Input() scrollPosition: number;
	@Input() variantOrModelIsNew: boolean = false;
	@Input() showNoneChoiceOption: boolean = true;
	@Input() matchColor: number = 0;
	@Output() scrollPositionChange = new EventEmitter<number>();
	@Output() selectedColorChange = new EventEmitter<VariantColorModel>();
	@Output() selectedColorGroupChange = new EventEmitter<ColorGroupModel | null>();
	@Output() colorMatchChange = new EventEmitter<VariantColorModel>();
	@Output() miscMatchChange = new EventEmitter<MiscModel>();

	public availableColors: Array<VariantColorFromApiModel> = [];
	public selectableColors: Array<VariantColorFromApiModel | null> = [];
	public selectableGroups: Array<ColorGroupModel | null | undefined> = [];
	public translation: string;

	constructor(
		public imageService: ImageService,
		private sorterService: SorterService,
	) { }

	ngOnInit() {
		this.setAvailableColors();
		this.selectableGroups = this.getColorGroups();
		this.setSelectedColorGroup();
		this.translation = `CUSTOMIZER.COMPONENTS.${this.translateName}`;
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.scrollIntoView();
		},
			500);
	}

	public setAvailableColors() {
		this.availableColors = [];
		let colorsOnVariants = new Array<VariantColorFromApiModel>();

		for (const variant of this.variants.filter(x => !x.isDisabled)) {
			if (variant.VariantSizes && variant.VariantSizes.filter(x => !x.isDisabled).length > 0) {
				const variantColors = variant.VariantSizes.filter(x => !x.isDisabled).map(x => x.VariantColors).reduce((prev, next) => prev.concat(next)).filter(x => !x.isDisabled);
				colorsOnVariants = colorsOnVariants.concat(variantColors);
			}
		}

		if (!this.variants.every((val, i, arr) => val.Component.Code == arr[0].Component.Code)) {
			colorsOnVariants.forEach(color => {
				const colorExistsOnAllColors = colorsOnVariants.filter(x => x.Color?.Code == color.Color?.Code).length == this.variants.length;

				if (colorExistsOnAllColors) {
					if (!this.availableColors.some(x => x.Color?.Code == color.Color?.Code)) {
						this.availableColors.push(color);
					}
				}
			});
		}
		else {
			this.availableColors = this.stripDuplicateColors(colorsOnVariants);
		}
	}

	private stripDuplicateColors(colors: Array<VariantColorFromApiModel>): Array<VariantColorFromApiModel> {
		const colorUuids: Array<string> = [];
		let availableColors = colors.flatMap(x => x) ?? []

		colors.forEach(color => {
			if (colorUuids.findIndex(c => c === color.ColorUuid) > -1) {
				let colorIndex = availableColors.findIndex(c => c.ColorUuid === color.ColorUuid);
				availableColors.splice(colorIndex, 1);
			}

			colorUuids.push(color.ColorUuid);
		});

		return availableColors;
	}

	getColorGroups(): Array<ColorGroupModel | null | undefined> {
		let groups = new Array<ColorGroupModel | null | undefined>();

		groups = this.availableColors.filter(x => x.Color?.ColorGroup != null)
			.map(x => x.Color?.ColorGroup)
			.filter((x, i, self) => self.findIndex(s => s?.Id == x?.Id) == i)
			.sort(this.sorterService.sortByColorGroup);

		if (this.availableColors.some(x => x.Color?.ColorGroup == null)) {
			const description = DataTranslationModel.GetFallbackOnlyModel("CUSTOMIZER.COMPONENTS.COLOR.OTHER");
			const otherGroup = new ColorGroupModel();
			otherGroup.Code = "OTHER";
			otherGroup.Description = description;
			otherGroup.Order = "*";
			otherGroup.Id = 0;
			groups.push(otherGroup);
		}

		return groups;
	}

	private setSelectedColorGroup() {
		if (!this.useColorGroup || !this.selectableGroups || this.selectableGroups.length == 0) {
			this.setColorGroup(null);
		}
		else {
			if (this.selectedColorGroup && this.selectableGroups.some(sg => sg?.Code == this.selectedColorGroup?.Code)) {
				this.setColorGroup(this.selectedColorGroup);
			}
			else if (this.selected && this.selected.Color && this.selected.Color.ColorGroup) {
				const colorGroupFromColor = this.selectableGroups.find(x => x?.Code == this.selected?.Color?.ColorGroup?.Code);
				this.setColorGroup(colorGroupFromColor);
			}
			else {
				this.setColorGroup(this.selectableGroups[0]);
			}
		}
	}

	setColorGroup(colorGroup: ColorGroupModel | null | undefined) {
		this.selectedColorGroupChange.emit(colorGroup);
		this.selectedColorGroup = colorGroup;
		this.selectableColors = this.getSelectableColors(colorGroup);

		this.selectableColors.forEach(color => {
			if (color) {
				const colorUrl = this.imageService.GetColorImageUrl(color.Color?.ColorLine?.Code, color.Color?.Code);
				const image = new Image();
				image.src = colorUrl;
			}
		});
	}

	private getSelectableColors(colorGroup?: ColorGroupModel | null): Array<VariantColorFromApiModel | null> {

		let availableColorsResult: VariantColorFromApiModel[] = this.availableColors;

		if (!colorGroup) {
			// use all colors
			availableColorsResult = this.availableColors;

		} else if (colorGroup.Code === 'OTHER') {
			// colors without a group
			availableColorsResult = this.availableColors.filter(x => x.Color?.ColorGroup == null);
		} else {
			// colors in the specific group
			availableColorsResult = this.availableColors.filter(x => x.Color?.ColorGroup && x.Color.ColorGroup.Id == colorGroup.Id);
		}


		const sorted = this.SortColorsByOrder(availableColorsResult);

		if (this.variants?.[0]?.Optional) {

			return [null, ...sorted]; // ensure Optional appears first
		}

		return sorted;
	}

	private SortColorsByOrder(colorsInput: Array<VariantColorFromApiModel>): Array<VariantColorFromApiModel> {

		const sorted = [...colorsInput].sort((a, b) =>
			(a.Order ?? '').localeCompare(
				b.Order ?? '',
				undefined,
				{ numeric: true, sensitivity: 'base' }
			)
		);

		return sorted
	}

	public setSelected(color: VariantColorFromApiModel | null) {
		this.selectedColorChange.emit(color?.ToVariantColor());
	}

	private scrollIntoView() {
		if (this.selected && this.selected.ColorUuid) {
			const parent = document.querySelectorAll('#colorlist');

			if (parent) {
				parent.forEach((list) => {
					const item = list.querySelector('[data-guid="' + this.selected?.ColorUuid + '"]');

					if (item) {
						item.scrollIntoView({ behavior: 'smooth' });
					}
				});
			}
		}
	}

	public matchColors(model: VariantColorFromApiModel | null) {
		this.colorMatchChange.emit(model?.ToVariantColor());
	}

	public miscMatch(model: MiscModel) {
		this.miscMatchChange.emit(model);
	}

	updatePosition(pos: any) {
		this.scrollPositionChange.emit(pos);
	}

}
